import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import { EpisodeDetailsHeader } from '@insights_module/components/episode_details_screen/EpisodeDetailsHeader';
import { EpisodeDetailsSidebar } from '@insights_module/components/episode_details_screen/EpisodeDetailsSidebar';
import { GarmFilterController } from '@insights_module/components/episode_details_screen/GarmFilterController';
import { ContextualFilterController } from '@insights_module/components/episode_details_screen/ContextualFilterController';
import { EntitiesFilterController } from '@insights_module/components/episode_details_screen/EntitiesFilterController';
import { SearchFilterController } from '@insights_module/components/episode_details_screen/SearchFilterController';
import { EpisodeDetailsChangeViewSidebar } from '@insights_module/components/episode_details_screen/EpisodeDetailsChangeViewSidebar';
import { EpisodeDetailsSocialPostsHeader } from '@insights_module/components/episode_details_screen/EpisodeDetailsSocialPostsHeader';
import { useEpisodeSegment } from '@insights_module/hooks/useEpisodeSegment';
import {
  DETAILS_VIEW_ITEMS,
  PROMOTION_VIEW_ITEMS,
  INSIGHTS_VIEW_ITEMS,
  useEpisodeDetailsContext,
} from '@insights_module/context/EpisodeDetailsContext';
import { EpisodeDetailsAndPromotionContent } from '@insights_module/components/episode_details_screen/EpisodeDetailsAndPromotionContent';
import { EpisodeTranscript } from '@insights_module/components/episode_details_screen/EpisodeTranscript';

export const SIDEBAR_HEADER_HEIGHT = '42px';

const Container = tw.div`bg-neutral-background h-full w-full overflow-hidden shadow-ui border border-gray-3`;
const LayoutGrid = tw.div`w-full h-[calc(100% - ${SIDEBAR_HEADER_HEIGHT})] flex flex-row justify-between`;

const FlexItem = tw.div`h-full w-full`;
const FlexItemHeader = tw.div`px-3 border-b border-solid border-gray-3 h-[${SIDEBAR_HEADER_HEIGHT}] flex flex-row items-center justify-between bg-neutral-background`;
const Title = tw.p`font-semibold text-left`;
const RightControls = tw.div`flex flex-row items-center gap-2`;
const ContentWrapper = tw.div`flex flex-col w-full h-full`;
const TranscriptWrapper = tw.div`w-full h-[calc(100% - ${SIDEBAR_HEADER_HEIGHT})] relative bg-neutral-background`;

export const EpisodeDetailsDrawer = () => {
  const { t } = useTranslation();
  const { segmentsWithParagraphs } = useEpisodeSegment();
  const { activeView, setHasDiarizedTranscript } = useEpisodeDetailsContext();

  const hasDiarizedTranscript = segmentsWithParagraphs.some((segment) =>
    segment.paragraphData.some((paragraph) => paragraph.speaker !== null)
  );

  useEffect(() => {
    setHasDiarizedTranscript(hasDiarizedTranscript);
  }, [hasDiarizedTranscript]);

  return (
    <Container>
      <EpisodeDetailsHeader />
      <LayoutGrid>
        <EpisodeDetailsChangeViewSidebar />

        <FlexItem tw='bg-page-background'>
          {INSIGHTS_VIEW_ITEMS.includes(activeView) && (
            <FlexItemHeader>
              <Title>
                {t(`insights.episode_details_drawer.change_view_sidebar.${activeView}`)}
              </Title>
              <RightControls>
                <>
                  <GarmFilterController />
                  <ContextualFilterController />
                  <EntitiesFilterController />
                  <SearchFilterController />
                </>
              </RightControls>
            </FlexItemHeader>
          )}

          {[...PROMOTION_VIEW_ITEMS, ...DETAILS_VIEW_ITEMS].includes(activeView) && (
            <ContentWrapper>
              <EpisodeDetailsSocialPostsHeader />
              <EpisodeDetailsAndPromotionContent />
            </ContentWrapper>
          )}

          {INSIGHTS_VIEW_ITEMS.includes(activeView) && (
            <TranscriptWrapper>
              <EpisodeTranscript transcript={segmentsWithParagraphs} />
            </TranscriptWrapper>
          )}
        </FlexItem>

        {INSIGHTS_VIEW_ITEMS.includes(activeView) && <EpisodeDetailsSidebar />}
      </LayoutGrid>
    </Container>
  );
};
